import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PaidIcon from '@mui/icons-material/Paid';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import dayjs from 'dayjs';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import {
  autoChargeInvoice,
  downloadInvoicePdf,
  deleteInvoice,
  markInvoicePaid,
  recordPayment,
  resendInvoiceReceipt,
  retrieveInvoice,
  sendInvoice,
  voidInvoice,
  waiveLateStrike,
} from '@src/api/invoices';
import PaymentsIcon from '@mui/icons-material/Payments';
import BoltIcon from '@mui/icons-material/Bolt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { DEFAULT_ACH_INSTRUCTIONS } from '@src/constants/invoicing';

const statusColor = {
  draft: 'default',
  sent: 'info',
  outstanding: 'warning',
  void: 'default',
  paid: 'success',
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDateTime = (value) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—');
const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');
const formatPaymentSourceLabel = (value) => {
  if (!value) {
    return '—';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'stripe') {
    return 'Stripe';
  }
  if (normalized === 'manual') {
    return 'Manual';
  }
  return normalized;
};
const formatPaymentMethodLabel = (value) => {
  if (!value) {
    return '—';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'ach_wire') {
    return 'ACH/Wire';
  }
  if (normalized === 'us_bank_account') {
    return 'ACH';
  }
  if (normalized === 'check') {
    return 'Check';
  }
  if (normalized === 'card') {
    return 'Card';
  }
  return normalized.replace(/_/g, ' ');
};

const getPaymentState = (invoice) => {
  const paymentState = String(invoice?.payment_state || '').toLowerCase();
  if (paymentState) {
    return paymentState;
  }
  if (String(invoice?.status || '').toLowerCase() === 'paid') {
    return 'paid';
  }
  if (invoice?.payment_failed_at) {
    return 'failed';
  }
  if (String(invoice?.payment_source || '').toLowerCase() === 'stripe') {
    return 'processing';
  }
  return 'none';
};

const paymentStatusConfig = {
  paid: { label: 'Paid', color: 'success' },
  partial: { label: 'Partial', color: 'warning' },
  unpaid: { label: 'Unpaid', color: 'default' },
  void: { label: 'Void', color: 'default' },
};

const getPaymentStatusPresentation = (invoice) => {
  const ps = String(invoice?.payment_status || 'unpaid').toLowerCase();
  return paymentStatusConfig[ps] || paymentStatusConfig.unpaid;
};

const getStatusPresentation = (invoice) => {
  const status = String(invoice?.status || '').toLowerCase();
  const dueDate = invoice?.due_date;
  const overdue =
    status === 'outstanding' &&
    dueDate &&
    dayjs(dueDate).isValid() &&
    dayjs(dueDate).isBefore(dayjs().startOf('day'));

  if (overdue) {
    return { label: 'OVERDUE', color: 'error' };
  }
  return {
    label: status ? status.toUpperCase() : 'UNKNOWN',
    color: statusColor[status] || 'default',
  };
};

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const message = useMessage();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [resendingReceipt, setResendingReceipt] = useState(false);
  const [sendMessage, setSendMessage] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [manualPaymentMethod, setManualPaymentMethod] = useState('us_bank_account');
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('us_bank_account');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [autoChargeDialogOpen, setAutoChargeDialogOpen] = useState(false);
  const [autoCharging, setAutoCharging] = useState(false);
  const [autoChargeMethod, setAutoChargeMethod] = useState('');
  const [waiveDialogOpen, setWaiveDialogOpen] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');

  const loadInvoice = useCallback(() => {
    if (!invoiceId) {
      return;
    }
    setLoading(true);
    setError('');
    retrieveInvoice(invoiceId)
      .then((res) => {
        setInvoice(res.data);
      })
      .catch((err) => {
        console.error('Failed to load invoice', err);
        setError('Unable to load invoice details.');
      })
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handleSend = () => {
    if (!invoiceId) {
      return;
    }
    setSending(true);
    sendInvoice(invoiceId, { message: sendMessage })
      .then((res) => {
        setInvoice(res.data);
        message.success('Invoice sent. Emails dispatched and payment link prepared.');
      })
      .catch((err) => {
        console.error('Failed to send invoice', err);
        message.error('Unable to send the invoice. Please confirm billing details and attachments.');
      })
      .finally(() => setSending(false));
  };

  const openMarkPaidDialog = () => {
    if (!invoice || invoice.status === 'paid' || invoice.status === 'void' || markingPaid) {
      return;
    }
    setManualPaymentMethod(invoice.payment_method || 'us_bank_account');
    setMarkPaidDialogOpen(true);
  };

  const closeMarkPaidDialog = () => {
    if (markingPaid) {
      return;
    }
    setMarkPaidDialogOpen(false);
  };

  const handleWaiveStrike = () => {
    if (!invoiceId || !waiveReason.trim() || waiving) {
      return;
    }
    setWaiving(true);
    waiveLateStrike(invoiceId, { waived: true, reason: waiveReason.trim() })
      .then(() => {
        setWaiveDialogOpen(false);
        setWaiveReason('');
        message.success("Strike waived — it no longer counts toward the client's standing.");
        loadInvoice();
      })
      .catch((err) => {
        console.error('Failed to waive strike', err);
        message.error(err?.response?.data?.detail || 'Unable to waive the strike.');
      })
      .finally(() => setWaiving(false));
  };

  const handleRestoreStrike = () => {
    if (!invoiceId || waiving) {
      return;
    }
    setWaiving(true);
    waiveLateStrike(invoiceId, { waived: false })
      .then(() => {
        message.success("Strike restored — it counts toward the client's standing again.");
        loadInvoice();
      })
      .catch((err) => {
        console.error('Failed to restore strike', err);
        message.error(err?.response?.data?.detail || 'Unable to restore the strike.');
      })
      .finally(() => setWaiving(false));
  };

  const handleResendReceipt = () => {
    if (!invoiceId || invoice?.status !== 'paid' || resendingReceipt) {
      return;
    }
    setResendingReceipt(true);
    resendInvoiceReceipt(invoiceId)
      .then((res) => {
        setInvoice(res.data);
        message.success('Payment receipt resent to billing contacts.');
      })
      .catch((err) => {
        console.error('Failed to resend receipt', err);
        message.error(err?.response?.data?.detail || 'Unable to resend the payment receipt.');
      })
      .finally(() => setResendingReceipt(false));
  };

  const handleMarkPaid = () => {
    if (!invoiceId) {
      return;
    }
    setMarkingPaid(true);
    markInvoicePaid(invoiceId, { payment_method: manualPaymentMethod })
      .then((res) => {
        setInvoice(res.data);
        setMarkPaidDialogOpen(false);
        message.success('Invoice marked as paid.');
      })
      .catch((err) => {
        console.error('Failed to mark invoice paid', err);
        message.error(err?.response?.data?.payment_method?.[0] || 'Unable to mark this invoice as paid.');
      })
      .finally(() => setMarkingPaid(false));
  };

  const openRecordPaymentDialog = () => {
    if (!invoice || invoice.status === 'paid' || invoice.status === 'void') {
      return;
    }
    setPaymentAmount(invoice.balance_due ? String(invoice.balance_due) : '');
    setPaymentMethod(invoice.payment_method || 'us_bank_account');
    setPaymentReference('');
    setPaymentNotes('');
    setRecordPaymentDialogOpen(true);
  };

  const closeRecordPaymentDialog = () => {
    if (recordingPayment) {
      return;
    }
    setRecordPaymentDialogOpen(false);
  };

  const handleRecordPayment = () => {
    if (!invoiceId || !paymentAmount) {
      return;
    }
    setRecordingPayment(true);
    recordPayment(invoiceId, {
      amount: paymentAmount,
      payment_method: paymentMethod,
      reference: paymentReference,
      notes: paymentNotes,
    })
      .then((res) => {
        setInvoice(res.data);
        setRecordPaymentDialogOpen(false);
        const newStatus = res.data?.payment_status;
        if (newStatus === 'paid') {
          message.success('Payment recorded. Invoice is now fully paid.');
        } else {
          message.success(`Payment of ${formatCurrency(paymentAmount)} recorded. Balance due: ${formatCurrency(res.data?.balance_due)}`);
        }
      })
      .catch((err) => {
        console.error('Failed to record payment', err);
        const detail = err?.response?.data?.detail || err?.response?.data?.amount?.[0] || 'Unable to record payment.';
        message.error(detail);
      })
      .finally(() => setRecordingPayment(false));
  };

  const autopayMethods = useMemo(() => {
    const consent = invoice?.client_autopay_consent;
    if (!consent || !consent.authorized) {
      return [];
    }
    return Array.isArray(consent.methods) ? consent.methods : [];
  }, [invoice]);

  const canAutoCharge = useMemo(() => {
    if (!invoice || autopayMethods.length === 0) {
      return false;
    }
    if (invoice.status === 'paid' || invoice.status === 'void' || invoice.status === 'draft') {
      return false;
    }
    return getPaymentState(invoice) !== 'processing';
  }, [invoice, autopayMethods]);

  const openAutoChargeDialog = () => {
    if (!canAutoCharge) {
      return;
    }
    setAutoChargeMethod(autopayMethods[0] || '');
    setAutoChargeDialogOpen(true);
  };

  const closeAutoChargeDialog = () => {
    if (autoCharging) {
      return;
    }
    setAutoChargeDialogOpen(false);
  };

  const handleAutoCharge = () => {
    if (!invoiceId || !autoChargeMethod) {
      return;
    }
    setAutoCharging(true);
    autoChargeInvoice(invoiceId, { payment_method: autoChargeMethod })
      .then((res) => {
        const result = res.data?.result || {};
        const updated = res.data?.invoice;
        if (updated) {
          setInvoice(updated);
        }
        setAutoChargeDialogOpen(false);
        if (result.status === 'succeeded') {
          message.success(
            `Auto-charged ${formatCurrency(result.amount)} to ${result.payment_method_brief || autoChargeMethod}.`,
          );
        } else if (result.status === 'processing') {
          message.success(
            `Charge of ${formatCurrency(result.amount)} is processing. We'll mark this invoice paid once it clears.`,
          );
        } else {
          message.error(result.failure_message || 'Auto-charge could not be completed.');
        }
      })
      .catch((err) => {
        console.error('Failed to auto-charge invoice', err);
        const data = err?.response?.data;
        const detail = data?.detail || data?.failure_message || 'Auto-charge failed.';
        message.error(detail);
      })
      .finally(() => setAutoCharging(false));
  };

  const handleDownloadPdf = useCallback(() => {
    if (!invoiceId) {
      return;
    }
    setDownloadingPdf(true);
    downloadInvoicePdf(invoiceId)
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `${invoice?.number || `invoice-${invoiceId}`}.pdf`;
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error('Failed to download invoice PDF', err);
        message.error('Unable to download the invoice PDF.');
      })
      .finally(() => setDownloadingPdf(false));
  }, [invoiceId, invoice, message]);

  const openDeleteDialog = () => {
    if (invoice?.status !== 'draft') {
      return;
    }
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) {
      return;
    }
    setDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    if (!invoiceId) {
      return;
    }
    setDeleting(true);
    deleteInvoice(invoiceId)
      .then(() => {
        message.success('Draft invoice deleted.');
        navigate(paths.dashboard.invoices.list);
      })
      .catch((err) => {
        console.error('Failed to delete invoice', err);
        message.error('Unable to delete this invoice.');
      })
      .finally(() => {
        setDeleting(false);
        setDeleteDialogOpen(false);
      });
  };

  const handleVoid = () => {
    if (!invoiceId) {
      return;
    }
    setVoiding(true);
    voidInvoice(invoiceId, { reason: voidReason })
      .then((res) => {
        setInvoice(res.data);
        setVoidDialogOpen(false);
        setVoidReason('');
        message.success('Invoice voided.');
      })
      .catch((err) => {
        console.error('Failed to void invoice', err);
        message.error(err?.response?.data?.detail || 'Unable to void this invoice.');
      })
      .finally(() => setVoiding(false));
  };

  const statusChip = useMemo(() => {
    if (!invoice) {
      return null;
    }
    const statusPresentation = getStatusPresentation(invoice);
    return (
      <Chip
        label={statusPresentation.label}
        color={statusPresentation.color}
        size="small"
      />
    );
  }, [invoice]);

  const amountAlerts = useMemo(() => {
    if (!invoice) {
      return null;
    }
    const alerts = [];
    if (invoice.needs_amount_confirmation) {
      alerts.push(
        <Alert key="needs-amount" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          This invoice has not been amount-confirmed. Review spend and manual adjustments before sending.
        </Alert>
      );
    }
    if (!invoice.line_items?.length) {
      alerts.push(
        <Alert key="missing-line-items" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          No line items are associated with this invoice. Add at least one before sending.
        </Alert>
      );
    }
    return alerts;
  }, [invoice]);

  const statusNotifications = useMemo(() => {
    if (!invoice) {
      return null;
    }
    const notices = [];
    if (invoice.sent_at) {
      notices.push(
        <Alert key="sent" severity="success" icon={<SendIcon fontSize="inherit" />}>
          Invoice emailed on {formatDateTime(invoice.sent_at)}.
        </Alert>
      );
    }
    if (invoice.outstanding_at) {
      notices.push(
        <Alert key="outstanding" severity="warning" icon={<InfoIcon fontSize="inherit" />}>
          {getStatusPresentation(invoice).label === 'OVERDUE'
            ? `Payment is overdue. Marked outstanding on ${formatDateTime(invoice.outstanding_at)}.`
            : `Marked outstanding on ${formatDateTime(invoice.outstanding_at)}.`}
        </Alert>
      );
    }
    if (invoice.status === 'void') {
      notices.push(
        <Alert key="void" severity="warning" icon={<BlockIcon fontSize="inherit" />}>
          Invoice voided{invoice.voided_at ? ` on ${formatDateTime(invoice.voided_at)}` : ''}.
          {invoice.void_reason ? ` Reason: ${invoice.void_reason}` : ''}
        </Alert>
      );
    }
    if (getPaymentState(invoice) === 'processing') {
      notices.push(
        <Alert key="processing" severity="info" icon={<InfoIcon fontSize="inherit" />}>
          Payment is processing with Stripe.
          {invoice.payment_method ? ` Method: ${formatPaymentMethodLabel(invoice.payment_method)}.` : ''}
        </Alert>
      );
    }
    if (invoice.paid_at) {
      notices.push(
        <Alert key="paid" severity="success" icon={<PaidIcon fontSize="inherit" />}>
          Payment confirmed on {formatDateTime(invoice.paid_at)}.
          {invoice.payment_source ? ` Source: ${formatPaymentSourceLabel(invoice.payment_source)}.` : ''}
          {invoice.payment_method ? ` Method: ${formatPaymentMethodLabel(invoice.payment_method)}.` : ''}
        </Alert>
      );
    }
    return notices;
  }, [invoice]);

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Stack spacing={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Alert severity="error">{error}</Alert>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadInvoice}>
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!invoice) {
    return null;
  }

  const totalLineItemAmount =
    invoice.line_items?.reduce(
      (sum, item) => sum + (Number(item.amount ?? item.line_total ?? 0) || 0),
      0
    ) || 0;
  const clientBreakdown = Array.isArray(invoice.client_breakdown_snapshot)
    ? invoice.client_breakdown_snapshot
    : [];

  return (
    <Box p={3}>
      <Stack spacing={2} mb={3} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Stack spacing={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.dashboard.invoices.list)}>
            Back to invoices
          </Button>
          <Typography variant="h4">{invoice.number || `Invoice #${invoice.id}`}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {statusChip}
            {invoice.payment_status && invoice.payment_status !== 'void' && (
              <Chip
                label={getPaymentStatusPresentation(invoice).label}
                color={getPaymentStatusPresentation(invoice).color}
                size="small"
                variant="outlined"
              />
            )}
            <Typography color="text.secondary">Total: {formatCurrency(invoice.total_amount)}</Typography>
            {invoice.payment_status === 'partial' && (
              <Typography color="warning.main" variant="body2">
                Paid: {formatCurrency(invoice.amount_paid)} · Balance: {formatCurrency(invoice.balance_due)}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Issued {formatDate(invoice.issue_date)} · Due {formatDate(invoice.due_date)}
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', md: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(paths.dashboard.invoices.edit(invoice.id))}
            disabled={invoice.status === 'paid' || invoice.status === 'void'}
          >
            Edit invoice
          </Button>
          <LoadingButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            loading={downloadingPdf}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            loading={sending}
            onClick={handleSend}
            disabled={sending || markingPaid || invoice?.needs_amount_confirmation || invoice.status === 'void'}
          >
            Send invoice
          </LoadingButton>
          {canAutoCharge && (
            <LoadingButton
              variant="contained"
              color="secondary"
              startIcon={<BoltIcon />}
              loading={autoCharging}
              onClick={openAutoChargeDialog}
              disabled={autoCharging}
            >
              Auto-charge
            </LoadingButton>
          )}
          <LoadingButton
            variant="contained"
            color="info"
            startIcon={<PaymentsIcon />}
            loading={recordingPayment}
            onClick={openRecordPaymentDialog}
            disabled={recordingPayment || invoice.status === 'paid' || invoice.status === 'void'}
          >
            Record payment
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="success"
            startIcon={<PaidIcon />}
            loading={markingPaid}
            onClick={openMarkPaidDialog}
            disabled={markingPaid || invoice.status === 'paid' || invoice.status === 'void'}
          >
            Mark paid
          </LoadingButton>
          {invoice.status === 'paid' && (
            <LoadingButton
              variant="outlined"
              color="success"
              startIcon={<MarkEmailReadIcon />}
              loading={resendingReceipt}
              onClick={handleResendReceipt}
              disabled={resendingReceipt}
            >
              Resend receipt
            </LoadingButton>
          )}
          <LoadingButton
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            loading={voiding}
            onClick={() => setVoidDialogOpen(true)}
            disabled={
              voiding ||
              invoice.status === 'paid' ||
              invoice.status === 'void' ||
              getPaymentState(invoice) === 'processing'
            }
          >
            Void invoice
          </LoadingButton>
          {invoice.status === 'draft' && (
            <LoadingButton
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              loading={deleting}
              onClick={openDeleteDialog}
              disabled={deleting}
            >
              Delete draft
            </LoadingButton>
          )}
        </Stack>
      </Stack>

      {invoice.late_strike && (
        <Alert
          severity={invoice.late_strike.waived ? 'info' : 'error'}
          sx={{ mb: 3 }}
          action={
            <LoadingButton
              color="inherit"
              size="small"
              loading={waiving}
              onClick={() =>
                invoice.late_strike.waived ? handleRestoreStrike() : setWaiveDialogOpen(true)
              }
            >
              {invoice.late_strike.waived ? 'Restore strike' : 'Waive strike'}
            </LoadingButton>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {invoice.late_strike.waived ? 'Late-payment strike (waived)' : 'Late-payment strike'}
          </Typography>
          <Typography variant="body2">
            {invoice.late_strike.waived
              ? `Waived${invoice.late_strike.waived_by_name ? ` by ${invoice.late_strike.waived_by_name}` : ''}${invoice.late_strike.waive_reason ? ` — ${invoice.late_strike.waive_reason}` : ''}. Does not count toward the client's standing.`
              : `This invoice hit the day-6 pause on ${dayjs(invoice.late_strike.struck_at).format('MMM D, YYYY')} and counts toward the client's late-payment standing. At 3 strikes we reach out about moving them to client-funded ad spend — new contract first, then switch billing. Nothing switches automatically.`}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Invoice summary" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                {statusNotifications}
                {amountAlerts}
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Client</Typography>
                  <Typography variant="body2">{invoice.client_name}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing window</Typography>
                  <Typography variant="body2">
                    {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing contacts</Typography>
                  {invoice.billing_contacts_snapshot?.length ? (
                    <Stack spacing={0.5}>
                      {invoice.billing_contacts_snapshot.map((contact, index) => {
                        const name = contact?.name;
                        const email = contact?.email || contact?.address || '—';
                        return (
                          <Typography variant="body2" key={`${email}-${index}`}>
                            {name ? `${name} • ` : ''}{email}
                          </Typography>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2">No billing contacts on file.</Typography>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Billing address</Typography>
                  {invoice.billing_address_snapshot && Object.keys(invoice.billing_address_snapshot).length ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {[
                        invoice.billing_address_snapshot?.line1,
                        invoice.billing_address_snapshot?.line2,
                        [
                          invoice.billing_address_snapshot?.city,
                          invoice.billing_address_snapshot?.state,
                          invoice.billing_address_snapshot?.postal_code,
                        ]
                          .filter(Boolean)
                          .join(', '),
                        invoice.billing_address_snapshot?.country,
                      ]
                        .filter(Boolean)
                        .join('\n')}
                    </Typography>
                  ) : (
                    <Typography variant="body2">No billing address captured.</Typography>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Line items total</Typography>
                  <Typography variant="body2">{formatCurrency(totalLineItemAmount)}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Amount paid</Typography>
                  <Typography variant="body2">{formatCurrency(invoice.amount_paid || 0)}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Balance due</Typography>
                  <Typography variant="body2" color={Number(invoice.balance_due) > 0 ? 'warning.main' : 'success.main'}>
                    {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Payment details</Typography>
                  <Typography variant="body2">
                    Source: {formatPaymentSourceLabel(invoice.payment_source)}
                  </Typography>
                  <Typography variant="body2">
                    Method: {formatPaymentMethodLabel(invoice.payment_method)}
                  </Typography>
                  <Typography variant="body2">
                    Date: {formatDateTime(invoice.paid_at)}
                  </Typography>
                  <Typography variant="body2">
                    Void date: {formatDateTime(invoice.voided_at)}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Client message</Typography>
                  <Typography variant="body2">{invoice.external_notes || '—'}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Internal notes</Typography>
                  <Typography variant="body2">{invoice.notes || '—'}</Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">ACH instructions</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {invoice.ach_instructions_snapshot || DEFAULT_ACH_INSTRUCTIONS}
                  </Typography>
                </Stack>
                {invoice.public_invoice_url && (
                  <Alert severity="info">
                    Invoice public link generated:{' '}
                    <Link href={invoice.public_invoice_url} target="_blank" rel="noopener noreferrer">
                      {invoice.public_invoice_url}
                    </Link>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Send options" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Message to include"
                  multiline
                  minRows={3}
                  value={sendMessage}
                  onChange={(event) => setSendMessage(event.target.value)}
                  placeholder="Optional note to include in the invoice email."
                />
                <Alert severity="info">
                  Sending the invoice will attach the PDF, include uploaded attachments, generate a Stripe payment link, and
                  deliver the email to billing contacts.
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => {
                    const historyEl = document.getElementById('invoice-history');
                    if (historyEl) {
                      historyEl.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Jump to history
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Line items" />
            <Divider />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.line_items?.length ? (
                    <>
                      {invoice.line_items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount ?? item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2">{formatCurrency(totalLineItemAmount)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography color="text.secondary">No line items found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        {clientBreakdown.length ? (
          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader title="Client breakdown" />
              <Divider />
              <CardContent>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Spend</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Line total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientBreakdown.map((entry, index) => (
                      <TableRow key={`${entry.client_id || index}-${entry.client_name || 'client'}`}>
                        <TableCell>{entry.client_name || '—'}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.spend_amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.fee_amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        ) : null}
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Attachments" />
            <Divider />
            <CardContent>
              {invoice.attachments?.length ? (
                <List dense>
                  {invoice.attachments.map((attachment) => (
                    <ListItem key={attachment.id} secondaryAction={
                      <IconButton
                        edge="end"
                        component="a"
                        href={attachment.file}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadIcon />
                      </IconButton>
                    }>
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.original_filename || 'Attachment'}
                        secondary={`Uploaded ${formatDateTime(attachment.uploaded_at)}${attachment.notes ? ` • ${attachment.notes}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No attachments uploaded yet.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {invoice.payments?.length > 0 && (
        <Box mt={3}>
          <Card>
            <CardHeader title="Payment history" />
            <Divider />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Recorded by</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{formatDateTime(payment.paid_at)}</TableCell>
                      <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                      <TableCell>{formatPaymentMethodLabel(payment.payment_method)}</TableCell>
                      <TableCell>{payment.reference || '—'}</TableCell>
                      <TableCell>{payment.recorded_by_name || '—'}</TableCell>
                      <TableCell>{payment.notes || '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Total paid</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">{formatCurrency(invoice.amount_paid)}</Typography>
                    </TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Box>
      )}

      <Box mt={3} id="invoice-history">
        <Card>
          <CardHeader title="Status history" />
          <Divider />
          <CardContent>
            {invoice.status_history?.length ? (
              <List>
                {invoice.status_history.map((entry) => (
                  <ListItem key={entry.id}>
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        entry.from_status &&
                        entry.to_status &&
                        entry.from_status !== entry.to_status
                          ? `${entry.from_status?.toUpperCase()} → ${entry.to_status?.toUpperCase()}`
                          : entry.notes || `${entry.to_status?.toUpperCase() || 'UPDATED'}`
                      }
                      secondary={[
                        formatDateTime(entry.changed_at),
                        entry.changed_by_name || '',
                        entry.from_status &&
                        entry.to_status &&
                        entry.from_status !== entry.to_status
                          ? entry.notes || ''
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">No status transitions recorded.</Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete draft invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">
              This will permanently delete{' '}
              <strong>{invoice?.number || `Invoice #${invoice?.id}`}</strong>.
            </Typography>
            <Alert severity="warning">This action cannot be undone.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <LoadingButton color="error" variant="contained" loading={deleting} onClick={handleDelete}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={markPaidDialogOpen} onClose={closeMarkPaidDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Mark invoice paid?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              Choose the payment method for <strong>{invoice?.number || `Invoice #${invoice?.id}`}</strong>.
            </Typography>
            <TextField
              select
              label="Payment method"
              value={manualPaymentMethod}
              onChange={(event) => setManualPaymentMethod(event.target.value)}
              fullWidth
            >
              <MenuItem value="ach_wire">ACH/Wire</MenuItem>
              <MenuItem value="us_bank_account">ACH</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="card">Card</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMarkPaidDialog} disabled={markingPaid}>
            Cancel
          </Button>
          <LoadingButton color="success" variant="contained" onClick={handleMarkPaid} loading={markingPaid}>
            Mark paid
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={voidDialogOpen} onClose={() => !voiding && setVoidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Void invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Voiding keeps this invoice in history, but disables it for active billing and payment collection.
            </Alert>
            <TextField
              label="Reason"
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              multiline
              minRows={3}
              placeholder="Optional reason for voiding this invoice"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !voiding && setVoidDialogOpen(false)} disabled={voiding}>
            Cancel
          </Button>
          <LoadingButton color="warning" onClick={handleVoid} loading={voiding}>
            Void invoice
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={waiveDialogOpen}
        onClose={() => !waiving && setWaiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Waive late-payment strike?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Waiving removes this invoice from the client&apos;s late-payment standing. Use it when
              the strike wasn&apos;t the client&apos;s fault — our billing error, a disputed invoice,
              or a payment-processor failure.
            </Alert>
            <TextField
              label="Reason"
              value={waiveReason}
              onChange={(event) => setWaiveReason(event.target.value)}
              multiline
              minRows={3}
              placeholder="Why doesn't this late payment count?"
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !waiving && setWaiveDialogOpen(false)} disabled={waiving}>
            Cancel
          </Button>
          <LoadingButton onClick={handleWaiveStrike} loading={waiving} disabled={!waiveReason.trim()}>
            Waive strike
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={recordPaymentDialogOpen} onClose={closeRecordPaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Invoice total: {formatCurrency(invoice?.total_amount)} ·
              Already paid: {formatCurrency(invoice?.amount_paid || 0)} ·
              Balance due: {formatCurrency(invoice?.balance_due ?? invoice?.total_amount)}
            </Alert>
            <TextField
              label="Payment amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText="Enter the amount received for this payment"
            />
            <TextField
              select
              label="Payment method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="ach_wire">ACH/Wire</MenuItem>
              <MenuItem value="us_bank_account">ACH</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="card">Card</MenuItem>
            </TextField>
            <TextField
              label="Reference"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              fullWidth
              placeholder="Check number, transaction ID, etc."
            />
            <TextField
              label="Notes"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="Optional notes about this payment"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecordPaymentDialog} disabled={recordingPayment}>
            Cancel
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            onClick={handleRecordPayment}
            loading={recordingPayment}
            disabled={!paymentAmount || Number(paymentAmount) <= 0}
          >
            Record payment
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={autoChargeDialogOpen} onClose={closeAutoChargeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Auto-charge this invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              This client has authorized auto-charge. Stripe will be charged for the
              outstanding balance off-session.
            </Alert>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>{invoice?.number || `Invoice #${invoice?.id}`}</strong>
              </Typography>
              <Typography variant="body2">
                Balance due: <strong>{formatCurrency(invoice?.balance_due ?? invoice?.total_amount)}</strong>
              </Typography>
            </Stack>
            {autopayMethods.length > 1 ? (
              <TextField
                select
                label="Payment method"
                value={autoChargeMethod}
                onChange={(event) => setAutoChargeMethod(event.target.value)}
                fullWidth
              >
                {autopayMethods.includes('card') && (
                  <MenuItem value="card">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CreditCardIcon fontSize="small" />
                      <span>Saved card</span>
                    </Stack>
                  </MenuItem>
                )}
                {autopayMethods.includes('us_bank_account') && (
                  <MenuItem value="us_bank_account">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccountBalanceIcon fontSize="small" />
                      <span>Saved bank (ACH)</span>
                    </Stack>
                  </MenuItem>
                )}
              </TextField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {autoChargeMethod === 'card'
                  ? 'Charging the saved card on file.'
                  : autoChargeMethod === 'us_bank_account'
                    ? 'Charging the saved bank account (ACH) on file.'
                    : ''}
              </Typography>
            )}
            {autoChargeMethod === 'us_bank_account' && (
              <Alert severity="warning" variant="outlined">
                ACH debits typically take a few business days to clear. The invoice will
                stay in <em>processing</em> until Stripe confirms the payment.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAutoChargeDialog} disabled={autoCharging}>
            Cancel
          </Button>
          <LoadingButton
            color="secondary"
            variant="contained"
            onClick={handleAutoCharge}
            loading={autoCharging}
            disabled={!autoChargeMethod}
            startIcon={<BoltIcon />}
          >
            Charge now
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
